// kfe-item-nav-bar.js — universal in-lesson item-navigation bar (Brief 167).
//
// ─────────────────────────────────────────────────────────────────────────────
// iOS CANON (read 2026-06-19 from ~/Developer/KFE-4.0-App; Rule #9)
// ─────────────────────────────────────────────────────────────────────────────
// Host: KFEModules/Main/MediaViewController.m. iOS shows ONE control bar at the
// bottom of every MEDIA-item screen (audio / video / HTML). It is a SINGLE
// storyboard layout with per-item-type conditional visibility — not three bars.
//
// Scope finding (the brief's open question, MediaViewController.m vs the exercise
// VCs): the bar lives on MediaViewController ONLY. Exercise screens (HAAI,
// True/False, Multiple-Choice, L2B, meters) have their own view controllers and
// do NOT host this bar. `mediaArray` (SectionViewController.m:80-83) is the
// lesson's MEDIA-playable subset — items with a `.media` file — so prev/next
// step between media items, skipping gates/meters/exercises. We therefore mount
// this component on the media-item screens only (kfe-video / kfe-html; audio is
// covered by <kfe-audio-player>, the audio-scrubber-mode bar from Brief 138).
//
// IBOutlet inventory (MediaViewController.m @interface, lines 30-94):
//   previousButton  ⏮   image "PrevSong"   — hidden when mediaArray.count<=1 (l.483)
//   nextButton      ⏭   image "NextSong" / "NextSongDisable" — .enabled flips per
//                       gate (isItemAccessibleWithItemID:, l.1848-1868) & last-item
//                       (l.495/511/514). Disabled = iOS native dimmed image.
//   top/bottomPlayCount  "CircleArr" (incomplete) → "WhiteTick" (complete) +
//                       label "%d/%d" completed/required (l.1248-1286). The label
//                       hides and the tick shows once completedCount>=requiredPlay.
//   upper/lowerAddToGlobalPlaylistButton  "MediaNotInGlobalPlaylist" (add) ↔
//                       "MediaInGlobalPlaylist" (in list) toggle (l.619-625);
//                       action addToGlobalPlaylistButtonAction: (l.2058).
//   playButton / back15 / go15 / scrubber — the PLAYBACK transport. On web this
//                       is the medium's own control (native <video controls>,
//                       Brief 036; <kfe-audio-player>, Brief 138), so it is NOT
//                       reproduced here — a ratified web-platform substitution,
//                       not a new divergence.
//   infoButton  "Information-on" — shown only for categoryLetter=="K" (l.526);
//                       tap toggles infoLabel (the item's Chinese title, l.2231).
//                       Deferred: category-letter data isn't surfaced in the web
//                       content model yet — flagged in the brief handover.
//
// ASCII sketch (web bar — video / HTML, scrubber-hidden mode):
//   ┌──────────────────────────────────────────────────────────┐
//   │  ‹‹            [ 0/3 | ✓ ]   [ ♪+ | ♪ ]              ›› │
//   │  prev          playcount     add-to-playlist        next  │
//   └──────────────────────────────────────────────────────────┘
//
// ─────────────────────────────────────────────────────────────────────────────
// This is a SMART shared widget: it resolves its own lesson context from the
// itemId (active-lesson setting, else reverse-lookup — the same deep-link path as
// kfe-audio-exercise), computes gate-aware prev/next via lesson-nav.computeItemNav
// (Brief 138/135), reads the playcount, and reflects Global-Playlist membership.
// It owns no media element, so a full re-render on state change is safe.
//
// Events (bubbling, composed) — observability + the brief's event contract:
//   itemNav:prev          { targetItemId, lessonId }
//   itemNav:next          { targetItemId, lessonId }
//   itemNav:playlist-add  { itemId, inPlaylist }
// Navigation is performed in-component (it already holds the target ids); the
// events let hosts/tests observe without re-implementing routing.

import { KFEComponent } from "../core/component.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { navigate } from "../router.js";
import { loadLesson } from "../data/pathway-loader.js";
import { resolveRequiredPlay } from "../data/item-completion.js";
import { computeItemNav, authoredRequiredPlay, bareItemId } from "../data/lesson-nav.js";
import { findLessonForItem } from "../data/lesson-lookup.js";

const DEFAULT_PATHWAY_ID = "kfe_zero_to_fluency";

const AUDIO_EXTS = [".m4a", ".mp3", ".ogg", ".wav", ".aac"];
const VIDEO_EXTS = [".mp4", ".mov", ".m4v"];

/** @param {string|null|undefined} name @param {string[]} exts */
function hasExt(name, exts) {
  if (!name) return false;
  const lower = String(name).toLowerCase();
  return exts.some((ext) => lower.endsWith(ext));
}

export class KFEItemNavBar extends KFEComponent {
  // `itemId` is the only required prop; lessonId/pathwayId are optional hints the
  // host can pass to skip the active-lesson/reverse lookup.
  static props = ["itemId", "lessonId", "pathwayId"];

  static styles = `
    :host { display: block; }
    .bar {
      display: flex; align-items: center; justify-content: space-between;
      gap: 0.75rem;
      background: var(--kfe-surface);
      border: 1px solid var(--kfe-border);
      border-radius: 12px;
      padding: 0.6rem 0.9rem;
      box-sizing: border-box;
    }
    .center { display: flex; align-items: center; gap: 0.75rem; }
    button {
      flex: 0 0 auto;
      display: inline-flex; align-items: center; justify-content: center;
      border: 1px solid var(--kfe-border);
      background: transparent; color: var(--kfe-fg);
      border-radius: 50%;
      width: 2.75rem; height: 2.75rem;
      line-height: 1; cursor: pointer; padding: 0;
    }
    button:disabled { opacity: var(--kfe-locked-opacity, 0.4); cursor: default; }
    /* Prev/next chevrons — match <kfe-audio-player>'s nav arrows so the two
       media bars read as the same control family. */
    button.nav { font-size: 1.6rem; font-weight: 800; letter-spacing: -0.08em; }
    button.playlist { font-size: 1.2rem; }
    button.playlist.in-list { color: var(--kfe-accent); border-color: var(--kfe-accent); }
    /* Playcount badge — white-on-accent pill, matching the audio/video badge. */
    .badge {
      display: inline-flex; align-items: center; justify-content: center;
      font-variant-numeric: tabular-nums;
      font-size: 0.85rem; font-weight: 700; line-height: 1;
      color: #fff; background: var(--kfe-accent);
      border-radius: 999px; padding: 0.35rem 0.65rem; min-width: 1.75rem;
    }
    .badge[hidden] { display: none; }
    .badge.complete { background: var(--kfe-success, #2e7d32); }
  `;

  constructor() {
    super();
    /** @type {Array<any>} */ this._lessonItems = [];
    /** @type {string|number|null} */ this._lessonId = null;
    this._gatingEnabled = true;
    /** @type {number|null} */ this._prevId = null;
    /** @type {number|null} */ this._nextId = null;
    this._prevEnabled = false;
    this._nextEnabled = false;
    this._completed = 0;
    this._required = 0;
    this._canPlaylist = false;
    /** @type {"present"|"removed"|"absent"} */ this._gpMembership = "absent";
    this._resolved = false;
    /** @type {() => void} */ this._onChange = () => {};
  }

  async onConnected() {
    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    // Completing a play bumps completedCount and may open the gate ahead
    // (Brief 102/138); a meter bump (Brief 125) raises requiredPlay. Either way
    // re-resolve so the badge + next-arrow lock track live.
    this._onChange = () => { this._refresh().catch(() => {}); };
    document.addEventListener("kfe:item-completed", this._onChange);
    document.addEventListener("kfe:item-progress-changed", this._onChange);
    this.addCleanup(() => {
      document.removeEventListener("kfe:item-completed", this._onChange);
      document.removeEventListener("kfe:item-progress-changed", this._onChange);
    });

    await services.items.loadContentItems().catch(() => {});
    await this._resolveContext();
    this._resolved = true;
    this._scheduleRender();
  }

  /** Resolve lesson context once: owning lesson, gating flag, ordered items. */
  async _resolveContext() {
    try {
      const id = Number(/** @type {any} */ (this).itemId);
      const learner = /** @type {any} */ (services.learner);

      let lessonId = /** @type {any} */ (this).lessonId ?? null;
      let pathwayId = /** @type {any} */ (this).pathwayId ?? null;
      let gatingSetting = null;
      [gatingSetting] = await Promise.all([
        Promise.resolve(learner.getSetting?.("gating_enabled")).catch(() => null),
      ]);
      if (!lessonId) {
        lessonId = await Promise.resolve(learner.getSetting?.("active_lesson")).catch(() => null);
      }
      if (!pathwayId) {
        pathwayId = await Promise.resolve(learner.getSetting?.("active_pathway")).catch(() => null);
      }
      this._gatingEnabled = gatingSetting !== false;

      // Deep-link path (Brief 142): no active lesson set — reverse-derive the
      // owning lesson from the item id so prev/next + the authored requiredPlay
      // resolve instead of degrading to a no-context bar.
      if (!lessonId && Number.isFinite(id)) {
        const derived = await findLessonForItem(id);
        if (derived) {
          lessonId = derived.lessonId;
          if (!pathwayId) pathwayId = derived.pathwayId;
        }
      }

      if (lessonId) {
        // Keep the raw lesson id (e.g. "lesson_103517") for the event payload —
        // it's the lesson-context handle, not a numeric item id.
        this._lessonId = lessonId;
        const pid = pathwayId ? String(pathwayId) : DEFAULT_PATHWAY_ID;
        try {
          const lesson = /** @type {any} */ (await loadLesson(pid, String(lessonId)));
          this._lessonItems = Array.isArray(lesson?.items) ? lesson.items : [];
        } catch {
          this._lessonItems = [];
        }
      }

      // Add-to-playlist is only meaningful for items with playable media (the GP
      // is a media queue; an HTML reading has nothing to enqueue). iOS shows the
      // button on all media items; we additionally hide it for media-less HTML —
      // a web-platform refinement noted in the brief handover.
      const item = Number.isFinite(id) ? services.items.getItem(id) : undefined;
      const anyItem = /** @type {any} */ (item);
      this._canPlaylist =
        hasExt(anyItem?.file1Name, AUDIO_EXTS) || hasExt(anyItem?.file2Name, AUDIO_EXTS) ||
        hasExt(anyItem?.file1Name, VIDEO_EXTS) || hasExt(anyItem?.file2Name, VIDEO_EXTS);

      await this._refresh();
    } catch (err) {
      console.warn("[item-nav-bar] context resolution failed", err);
    }
  }

  /** Recompute per-item progress, prev/next gating, and GP membership. */
  async _refresh() {
    const id = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(id)) return;
    const learner = /** @type {any} */ (services.learner);

    /** @type {Record<string, number>} */ const completedCounts = {};
    /** @type {Record<string, number|undefined>} */ const requiredPlays = {};
    await Promise.all((this._lessonItems || []).map(async (it) => {
      const numId = bareItemId(it?.id);
      if (!Number.isFinite(numId)) return;
      let prog;
      try { prog = await learner.getItemProgress?.(numId); } catch { prog = null; }
      completedCounts[String(numId)] = prog?.completedCount ?? 0;
      requiredPlays[String(numId)] = resolveRequiredPlay(
        { type: it?.type, requiredPlay: it?.requiredPlay },
        prog?.requiredPlay,
      );
    }));

    let myProg;
    try { myProg = await learner.getItemProgress?.(id); } catch { myProg = null; }
    const authored = authoredRequiredPlay(this._lessonItems, id);
    this._completed = myProg?.completedCount ?? 0;
    this._required = resolveRequiredPlay({ requiredPlay: authored }, myProg?.requiredPlay);

    const nav = computeItemNav(this._lessonItems, id, {
      gatingEnabled: this._gatingEnabled,
      completedCounts,
      requiredPlays,
    });
    this._prevId = nav.prevId;
    this._nextId = nav.nextId;
    this._prevEnabled = nav.prevEnabled;
    this._nextEnabled = nav.nextEnabled;

    if (this._canPlaylist) {
      try {
        this._gpMembership = await learner.getGlobalPlaylistMembership?.(id) ?? "absent";
      } catch { this._gpMembership = "absent"; }
    }

    if (this._connected) this._scheduleRender();
  }

  _goPrev() {
    if (!this._prevEnabled || this._prevId == null) return;
    this.emit("itemNav:prev", { targetItemId: this._prevId, lessonId: this._lessonId });
    navigate(`/exercise/${this._prevId}`);
  }

  _goNext() {
    if (!this._nextEnabled || this._nextId == null) return;
    this.emit("itemNav:next", { targetItemId: this._nextId, lessonId: this._lessonId });
    navigate(`/exercise/${this._nextId}`);
  }

  async _togglePlaylist() {
    const id = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(id)) return;
    const learner = /** @type {any} */ (services.learner);
    const wasIn = this._gpMembership === "present";
    try {
      if (wasIn) await learner.removeFromGlobalPlaylist?.(id);
      else await learner.addToGlobalPlaylistManual?.(id);
      this._gpMembership = wasIn ? "removed" : "present";
    } catch (err) {
      console.warn("[item-nav-bar] playlist toggle failed", err);
    }
    this.emit("itemNav:playlist-add", { itemId: id, inPlaylist: this._gpMembership === "present" });
    if (this._connected) this._scheduleRender();
  }

  render() {
    const frag = document.createDocumentFragment();
    const bar = document.createElement("div");
    bar.className = "bar";

    // Prev (left).
    const prev = this._makeButton("nav prev", "‹‹", "itemNav.previous", () => this._goPrev());
    prev.disabled = !this._prevEnabled;

    // Center group: playcount badge + add-to-playlist.
    const center = document.createElement("div");
    center.className = "center";

    const badge = document.createElement("span");
    badge.className = "badge";
    badge.setAttribute("role", "status");
    const required = Math.floor(Number(this._required) || 0);
    const completed = Math.max(0, Math.floor(Number(this._completed) || 0));
    if (required >= 1) {
      badge.hidden = false;
      if (completed >= required) {
        // iOS WhiteTick canon — a completed item shows a tick, not the count.
        badge.classList.add("complete");
        badge.textContent = "✓";
        badge.setAttribute("aria-label", t("itemNav.completed") || "Completed");
      } else {
        badge.textContent = `${completed}/${required}`;
        badge.setAttribute(
          "aria-label",
          t("itemNav.playCountLabel", { completed, required }) || `${completed}/${required}`,
        );
      }
    } else {
      badge.hidden = true;
    }
    center.appendChild(badge);

    if (this._canPlaylist) {
      const inList = this._gpMembership === "present";
      const playlist = this._makeButton(
        `playlist${inList ? " in-list" : ""}`,
        inList ? "♪" : "♪+",
        inList ? "itemNav.removeFromPlaylist" : "itemNav.addToPlaylist",
        () => { this._togglePlaylist().catch(() => {}); },
      );
      playlist.setAttribute("aria-pressed", String(inList));
      center.appendChild(playlist);
    }

    // Next (right).
    const next = this._makeButton("nav next", "››", "itemNav.next", () => this._goNext());
    next.disabled = !this._nextEnabled;

    bar.append(prev, center, next);
    frag.appendChild(bar);
    return frag;
  }

  /**
   * @param {string} cls @param {string} label @param {string} i18nKey
   * @param {() => void} onClick @returns {HTMLButtonElement}
   */
  _makeButton(cls, label, i18nKey, onClick) {
    const btn = /** @type {HTMLButtonElement} */ (document.createElement("button"));
    btn.type = "button";
    btn.className = cls;
    btn.textContent = label;
    btn.setAttribute("aria-label", t(i18nKey) || label);
    btn.addEventListener("click", onClick);
    return btn;
  }
}
